import React, { useEffect, useState, useContext } from 'react'
import { useTranslate, useLocaleState, useRedirect, useLogout } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { makeStyles } from '@mui/styles'
import { useLocation } from 'react-router-dom'
import { getGroups } from '@api'
import { selectedProperty, selectedGroup } from '@reducer/profile'
import { pathOnPortfolio } from '@hooks/usePortfolioUrlScope'
import { clearSession } from '@common/session'
import { AppContext } from '../context/AppContext'
import AddCircle from '@mui/icons-material/AddCircle'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { ACTIVATE_COMPANY_LOGO } from '../global'
import { GroupITF } from '@/types/group'
import { usePermissions } from '@hooks/useHasPermission'

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textField: {
    height: 48,
    backgroundColor: 'white',
    '&[class*=MuiOutlinedInput-root]': {
      padding: '0 9px',
    },
  },
  label: {
    transform: 'translate(14px, 12px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  inputPosition: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 20,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    color: '#7F8487',
    marginLeft: 20,
    fontWeight: 400,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  logout: {
    position: 'absolute',
    bottom: 40,
    textDecoration: 'underline',
    textAlign: 'center',
  },
}))

interface SearchProps {
  style?: React.CSSProperties
  isBarSearch?: boolean
  onlyShowDefault?: boolean
  onChange?: (group: GroupITF) => void
  className?: string
  inputRootClass?: string
  label?: string
  gId?: string
  variant?: TextFieldProps['variant']
  includeAllOption?: boolean
}

const ALL_GROUP_OPTION = { acId: '', GroupName: { en: 'All', es: 'All' } } as unknown as GroupITF

// Pre-scope portfolio paths: the shim rebuilds them onto the selected portfolio,
// so they need no navigation — including the portfolio dashboards that live
// outside /portfolioAnalytics. Exact '/properties' only: its create form is a
// half-filled record for the old portfolio.
const PORTFOLIO_PREFIXES = [
  '/portfolioAnalytics',
  '/portfolioTeam',
  '/workOrderDashboardGlobal',
  '/accountingDashboardGlobal',
  '/collectionDashboardGlobal',
  '/deficiencyTrackerGlobal',
]
/**
 * Where to go when the selected portfolio changes: stay on the current page,
 * mirroring pathForBuildingSwitch. Account Info and the /p/:gId portfolio
 * pages swap their :gId in place (keeping the section tab; a portfolio page
 * also drops the building ids, which belonged to the old portfolio), the
 * pre-scope portfolio paths stay put, and everything else — building pages,
 * whose building belongs to the old portfolio — falls back to the portfolio
 * landing page.
 */
const pathForGroupSwitch = (pathname: string, search: string, gId: string, fallback: string) => {
  const account = pathname.match(/^\/accountCreate\/[^/]+(.*)$/)
  if (account) return `/accountCreate/${gId}${account[1]}${search}`
  if (/^\/p\/[^/]+/.test(pathname)) return pathOnPortfolio(pathname, search, gId)
  if (pathname === '/properties' || PORTFOLIO_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return ''
  }
  return fallback
}

const Search = (props: SearchProps) => {
  const {
    style = {},
    isBarSearch,
    onlyShowDefault,
    onChange,
    className = '',
    inputRootClass,
    label,
    gId,
    variant,
    includeAllOption,
  } = props
  const { updateGroup } = useContext(AppContext)
  const { selectedGroupId, selectedGroupName } = useSelector((state) => state.profile)
  const groupId = typeof gId === 'undefined' ? selectedGroupId : gId
  const [loading, setLoading] = useState(true)
  const permissions = usePermissions()
  const { ManageEntities, GlobalManageCustomers } = permissions
  const [groups, setGroups] = useState<GroupITF[]>()
  const translate = useTranslate()
  const [locale] = useLocaleState()
  const redirectTo = useRedirect()
  const dispatch = useDispatch()
  const { pathname, search } = useLocation()
  const classes = useStyles()

  useEffect(() => {
    // GetGroups returns the full cross-customer account list and is gated server-side on
    // GlobalManageCustomers (AcAdmin only). Skip it for everyone else — otherwise the call
    // 403s and surfaces a permission error. Non-global users only ever see their own group.
    if (!GlobalManageCustomers) {
      setLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        const { Groups } = (await getGroups()) as any
        setGroups(Groups)
        setLoading(false)
      } catch (e) {
        setLoading(false)
      }
    }
    fetchData()
  }, [selectedGroupId, isBarSearch, GlobalManageCustomers])
  if (!groups && !onlyShowDefault && isBarSearch && !loading) return null
  const showGroups = onlyShowDefault
    ? [{ GroupName: { en: selectedGroupName, es: selectedGroupName }, acId: selectedGroupId } as unknown as GroupITF]
    : groups
  const option =
    (includeAllOption && groupId === '' ? ALL_GROUP_OPTION : undefined) ||
    showGroups?.find((v) => v.acId === groupId) ||
    (loading && selectedGroupId
      ? ({
          acId: selectedGroupId,
          GroupName: { [locale]: selectedGroupName, en: selectedGroupName },
        } as unknown as GroupITF)
      : '')
  const disabledBtn = isBarSearch && showGroups?.length === 1
  const groupOptions = includeAllOption ? [ALL_GROUP_OPTION, ...(showGroups || [])] : showGroups
  return (
    <Autocomplete
      id="free-solo-2-demo"
      disableClearable
      loading={loading}
      disabled={disabledBtn}
      className={className}
      classes={{ inputRoot: `${classes.textField} ${inputRootClass}` }}
      onChange={(_, group) => {
        const { acId, GroupName } = group as GroupITF
        if (onChange) {
          return onChange(group)
        }
        dispatch(selectedGroup({ groupId: acId, groupName: GroupName[locale], group }))
        updateGroup({ currentGroupId: acId, currentGroupName: GroupName[locale] })
        dispatch(selectedProperty({ propertyId: '', propertyName: '', buildingCode: '', propertyTag: {} }))
        // Stay where the user is (e.g. comparing Tenant Health across
        // portfolios) instead of yanking them to the Buildings list.
        const target = pathForGroupSwitch(pathname, search, acId, ManageEntities ? '/properties' : '/')
        if (target) redirectTo(target)
      }}
      value={option as GroupITF}
      style={style}
      options={groupOptions || []}
      getOptionLabel={(option: GroupITF) => option.GroupName?.[locale] || ''}
      isOptionEqualToValue={(option: GroupITF, value: GroupITF) => option.acId === value?.acId}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{ classes: { root: classes.label } }}
          placeholder={translate('pos.search_client')}
          label={label}
          variant={variant || 'outlined'}
        />
      )}
      popupIcon={variant !== 'standard' ? <ExpandMore /> : undefined}
    />
  )
}

interface Props {
  onlyShowDefault?: boolean
}

const AdminSearch: React.FC<Props> = ({ onlyShowDefault }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const logout = useLogout()
  const redirectTo = useRedirect()
  const { ManageGroups } = usePermissions()

  const oncreate = () => {
    redirectTo('/accountCreate')
  }
  const handleClick = async () => {
    // authProvider.logout already runs clearSession; the explicit call keeps
    // this path a full teardown even if the provider changes. Idempotent.
    await logout()
    clearSession()
  }
  return (
    <Box className={classes.root}>
      <Box sx={{ textAlign: 'left' }}>
        <img src={ACTIVATE_COMPANY_LOGO} style={{ height: 200, width: 600 }} alt="group logo" />
        <Box className={classes.inputPosition}>
          <AccountSearch style={{ width: '100%', maxWidth: 500 }} />
          {ManageGroups && (
            <Box className={classes.button} onClick={oncreate}>
              <AddCircle style={{ fontSize: 40, color: '#4A3D9E', marginRight: 7 }} />
              {translate('pos.create_client')}
            </Box>
          )}
        </Box>
      </Box>
      <Button className={classes.logout} onClick={handleClick}>
        Logout
      </Button>
    </Box>
  )
}

export default React.memo(AdminSearch)
export const AccountSearch = React.memo(Search)
